import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FastField, Form, Formik } from 'formik';
import { useStoreActions } from 'easy-peasy';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from 'yup';
import _e from 'common/localeUtil';
import GenericDialog from './GenericDialog';
import FieldWrapper from '../form/FieldWrapper';
import FormStatusComponent from '../form/FormStatusComponent';
import { MESSAGE_TYPES } from '../../constants';
import GMButton from '../button/GMButton';
import ToolTipCustomDomain from '../tooltip/ToolTipCustomDomain';

const useStyles = makeStyles(() => ({
	formWrapper: {
		'& input': {
			textTransform: 'lowercase',
		},
	},
}));

const EditUrlDialog = ({ title, loading, open, handleClose, handleChanged, defaultSubdomain }) => {
	const classes = useStyles();
	const changeSubdomain = useStoreActions((actions) => actions.template.changeSubdomain);
	const showMessage = useStoreActions((actions) => actions.showMessage.showMessage);
	const [errorMessage, setErrorMessage] = useState(null);

	const onSubmit = async ({ subdomain }, { setSubmitting }) => {
		const responseData = await changeSubdomain({ subdomain: subdomain.toLowerCase() });
		if (responseData?.success) {
			handleChanged(responseData?.subdomain);
			handleClose();
			showMessage({ text: 'Domain updated', type: MESSAGE_TYPES.SUCCESS });
		} else {
			setErrorMessage(responseData?.data?.error || 'Error');
		}
		setSubmitting(false);
	};

	const renderContent = () => {
		return (
			<Formik
				initialValues={{ subdomain: defaultSubdomain }}
				validateOnChange
				validationSchema={Yup.object({
					subdomain: Yup.string()
						.matches(/^[a-zA-Z0-9_-]+$/, {
							message: "Can only contain alphanumeric characters and '-'",
							excludeEmptyString: true,
						})
						.max(40, 'Must be 40 characters or less')
						.required('Required'),
				})}
				onSubmit={onSubmit}
			>
				{({ isValid, isSubmitting }) => (
					<Form className={classes.formWrapper}>
						<Box p={1}>
							<FastField name="subdomain" control="input" component={FieldWrapper} />
						</Box>
						{errorMessage && (
							<Box p={1}>
								<FormStatusComponent error={errorMessage} />
							</Box>
						)}
						<Box p={1}>
							<ToolTipCustomDomain />
						</Box>
						<Box
							p={1}
							justifyContent="flex-end"
							alignItems="center"
							flexDirection="row"
							display="flex"
						>
							<GMButton
								variant="contained"
								type="submit"
								disabled={!isValid}
								title="save"
								color="primary"
								loading={isSubmitting}
							>
								{_e('save')}
							</GMButton>
						</Box>
					</Form>
				)}
			</Formik>
		);
	};

	return (
		<GenericDialog
			open={open}
			loading={loading}
			handleClose={handleClose}
			content={renderContent()}
			title={title}
			showTitleDivider
			hideConfirmButton
		/>
	);
};

EditUrlDialog.propTypes = {
	open: PropTypes.bool,
	defaultSubdomain: PropTypes.string,
	loading: PropTypes.bool,
	handleClose: PropTypes.func,
	handleChanged: PropTypes.func,
	title: PropTypes.string,
};

export default EditUrlDialog;
